/**
 * HighlightElement — F12.H T12.H.2.
 *
 * „Wyróżnienie" — kolorowy banner-KONTENER nad zakładkami do podkreślenia
 * ważnej informacji na rekordzie (np. „Klient VIP", „Pilna sprawa"). Wzorem
 * SellCall: NIE jest polem tekstowym — trzyma prawdziwe dzieci (`children[]`),
 * do których przeciąga się pola/elementy z palety (drop target przez
 * `ContainerInCanvas` w `SystemModuleBuilderPage` / `ViewLevelBar`).
 *
 * Settings shape:
 *   { variant?: 'info'|'success'|'warning'|'danger',
 *     icon?: string (nazwa Lucide), title?: string }
 */

import { Sparkles } from 'lucide-react';

import { Dropdown } from '@/components/Dropdown';
import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';
import { Input } from '@/components/Input';
import { IconPicker } from '@/components/modules/IconPicker';
import { resolveIcon } from '@/lib/icon-map';

type HighlightVariant = 'info' | 'success' | 'warning' | 'danger';

const VARIANT_STYLES: Record<
  HighlightVariant,
  { bg: string; border: string; text: string; icon: string }
> = {
  // Kolory semantyczne STAŁE (nie zabarwiane akcentem tenanta) — patrz tailwind.config.js.
  info: {
    bg: 'bg-info-50',
    border: 'border-info-200',
    text: 'text-info-800',
    icon: 'text-info-500',
  },
  success: {
    bg: 'bg-success-50',
    border: 'border-success-200',
    text: 'text-success-800',
    icon: 'text-success-500',
  },
  warning: {
    bg: 'bg-warning-50',
    border: 'border-warning-200',
    text: 'text-warning-800',
    icon: 'text-warning-500',
  },
  danger: {
    bg: 'bg-danger-50',
    border: 'border-danger-200',
    text: 'text-danger-800',
    icon: 'text-danger-500',
  },
};

export const HighlightElement: LayoutElementDefinition = {
  type: 'highlight',
  category: 'struktura',
  // Kontener — pola/elementy przeciągnięte „na wyróżnienie" lądują w `children[]`,
  // nie w treści tekstowej (parytet z Panel/Card, wzorzec SellCall).
  isContainer: true,
  // View-level (T2): renderowany nad paskiem zakładek. Singleton (jeden na widok) —
  // wzorzec SellCall: stały slot „+ Dodaj wyróżnienie" nad zakładkami, nie klocek z palety.
  viewLevel: true,
  viewLevelSingleton: true,
  // Nie przeciąga się z palety — dodaje się wyłącznie przez slot nad zakładkami
  // (SystemModuleBuilderPage: addHighlightButton / addViewLevelElement('highlight')).
  hiddenFromPalette: true,
  label: 'Wyróżnienie',
  icon: 'Sparkles',
  defaultSettings: () => ({
    variant: 'info',
    icon: 'Sparkles',
    title: '',
  }),
  propertiesEditor: ({ settings, onChange }) => {
    const variant = (settings.variant as HighlightVariant) ?? 'info';
    const title = String(settings.title ?? '');
    const icon = String(settings.icon ?? 'Sparkles');
    return (
      <div className="space-y-3" data-testid="highlight-properties-editor">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Wariant</label>
          <Dropdown
            value={variant}
            onChange={(v) => onChange({ variant: v as HighlightVariant })}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
            data-testid="highlight-prop-variant"
            options={[
              { value: 'info', label: 'Informacja' },
              { value: 'success', label: 'Sukces' },
              { value: 'warning', label: 'Ostrzeżenie' },
              { value: 'danger', label: 'Niebezpieczeństwo' },
            ]}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Ikona</label>
          <IconPicker
            value={icon}
            onChange={(name) => onChange({ icon: name })}
            ariaLabel="Ikona"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">
            Tytuł (opcjonalnie)
          </label>
          <Input
            type="text"
            value={title}
            onChange={(e) => onChange({ title: e.target.value })}
            className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm"
            data-testid="highlight-prop-title"
          />
        </div>
      </div>
    );
  },
  render: ({ element, children, mode }) => {
    const settings = (element.settings ?? {}) as {
      variant?: HighlightVariant;
      icon?: string;
      title?: string;
    };
    const variant = settings.variant ?? 'info';
    const styles = VARIANT_STYLES[variant];
    const IconComponent = resolveIcon(settings.icon ?? 'Sparkles', Sparkles);
    const isBuilder = mode === 'builder';

    return (
      <div
        className={`rounded-lg border ${styles.border} ${styles.bg} p-3 ${isBuilder ? 'ring-2 ring-transparent hover:ring-accent-200 transition' : ''}`}
        data-testid={`highlight-element-${element.id}`}
        data-element-type="highlight"
      >
        <div className="mb-2 flex items-center gap-2">
          <IconComponent className={`h-4 w-4 shrink-0 ${styles.icon}`} aria-hidden="true" />
          {settings.title && (
            <p className={`text-sm font-semibold ${styles.text}`}>{settings.title}</p>
          )}
        </div>
        {children.length > 0 ? (
          <div className="space-y-2">{children}</div>
        ) : isBuilder ? (
          <p className={`text-xs italic ${styles.text} opacity-70`}>
            Puste — przeciągnij tutaj pola lub elementy
          </p>
        ) : null}
      </div>
    );
  },
};
